import { format, isValid, parse, parseISO } from 'date-fns';
import titleCase from 'platform/utilities/data/titleCase';

import {
  AOJS,
  APPEAL_TYPES,
  CHAMPVA_PROVIDERS,
  DICClaimTypeCodes,
  DOCKET_TYPES,
  STATUS_TYPES,
  addOrRemoveDependentClaimTypeCodes,
  appealTypes,
  disabilityCompensationClaimTypeCodes,
  eightStepPensionClaimTypeCodes,
  pensionClaimTypeCodes,
  survivorsPensionClaimTypeCodes,
  veteransPensionClaimTypeCodes,
} from './constants';

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Takes a format string and returns a function that formats the given date.
 * `date` must be in ISO format ex. 2020-01-28.
 */
export const buildDateFormatter = (formatString = 'MMMM d, yyyy') => {
  return date => {
    const parsed = parseISO(date);
    return isValid(parsed) ? format(parsed, formatString) : 'Invalid date';
  };
};

/**
 * Like buildDateFormatter, but returns null for a missing or unparseable
 * date instead of the 'Invalid date' string.
 */
export const buildSafeDateFormatter = (formatString = 'MMMM d, yyyy') => {
  const formatter = buildDateFormatter(formatString);
  return date => {
    if (!date) return null;
    const formatted = formatter(date);
    return formatted === 'Invalid date' ? null : formatted;
  };
};

export function formatDate(date) {
  const parsed =
    typeof date === 'string' && YYYY_MM_DD.test(date)
      ? parse(date, 'yyyy-MM-dd', new Date())
      : new Date(date);

  if (!isValid(parsed)) {
    return 'Invalid date';
  }

  return format(parsed, 'MMMM dd, yyyy');
}

const claimPhaseTypeStepMap = {
  CLAIM_RECEIVED: 'Step 1 of 8: Claim received',
  UNDER_REVIEW: 'Step 2 of 8: Initial review',
  GATHERING_OF_EVIDENCE: 'Step 3 of 8: Evidence gathering',
  REVIEW_OF_EVIDENCE: 'Step 4 of 8: Evidence review',
  PREPARATION_FOR_DECISION: 'Step 5 of 8: Rating',
  PENDING_DECISION_APPROVAL: 'Step 6 of 8: Preparing decision letter',
  PREPARATION_FOR_NOTIFICATION: 'Step 7 of 8: Final review',
  COMPLETE: 'Step 8 of 8: Claim decided',
};

export function getClaimPhaseTypeHeaderText(claimPhaseType) {
  return claimPhaseTypeStepMap[claimPhaseType];
}

/**
 * Filter evidence submissions for failed uploads within the last 30 days.
 * acknowledgementDate is set to 30 days after the submission failed (backend logic).
 * @param {Array} evidenceSubmissions - Array of evidence submission objects
 * @returns {Array} Filtered array of failed submissions within last 30 days
 */
export function getFailedSubmissionsWithinLast30Days(evidenceSubmissions) {
  if (!evidenceSubmissions || !Array.isArray(evidenceSubmissions)) {
    return [];
  }

  return evidenceSubmissions.filter(
    submission =>
      submission.uploadStatus === 'FAILED' &&
      submission.acknowledgementDate &&
      new Date().toISOString() <= submission.acknowledgementDate,
  );
}

const statusStepMap = {
  CLAIM_RECEIVED: 'Step 1 of 5: Claim received',
  INITIAL_REVIEW: 'Step 2 of 5: Initial review',
  EVIDENCE_GATHERING_REVIEW_DECISION:
    'Step 3 of 5: Evidence gathering, review, and decision',
  PREPARATION_FOR_NOTIFICATION: 'Step 4 of 5: Preparation for notification',
  COMPLETE: 'Step 5 of 5: Closed',
};

export function getStatusDescription(status) {
  return statusStepMap[status];
}

/**
 * @param {string} aoj
 * @returns full name of Agency of Original Jurisdiction (AOJ) abbreviation
 */
export function getAojDescription(aoj) {
  switch (aoj) {
    case AOJS.vba:
      return 'Veterans Benefits Administration';
    case AOJS.vha:
      return 'Veterans Health Administration';
    case AOJS.nca:
      return 'National Cemetery Administration';
    default:
      return 'Agency of Original Jurisdiction';
  }
}

/**
 * Returns the short status title for an appeal/decision-review item, derived
 * from its status type. The shared ClaimOrAppealCard only needs the title.
 *
 * @param {Object} appeal
 * @returns {string}
 */
export function getAppealStatusTitle(appeal) {
  const { status, aoj } = appeal.attributes;
  const statusType = status?.type || status;
  const aojDescription = getAojDescription(aoj);

  switch (statusType) {
    case STATUS_TYPES.pendingSoc:
      return 'A Decision Review Officer is reviewing your appeal';
    case STATUS_TYPES.pendingForm9:
      return 'Please review your Statement of the Case';
    case STATUS_TYPES.pendingCertification:
      return 'The Decision Review Officer is finishing their review of your appeal';
    case STATUS_TYPES.pendingCertificationSsoc:
    case STATUS_TYPES.remandSsoc:
      return 'Please review your Supplemental Statement of the Case';
    case STATUS_TYPES.pendingHearingScheduling:
      return "You're waiting for your hearing to be scheduled";
    case STATUS_TYPES.scheduledHearing:
      return 'Your hearing has been scheduled';
    case STATUS_TYPES.onDocket:
      return 'Your appeal is waiting to be sent to a judge';
    case STATUS_TYPES.atVso:
      return 'Your appeal is with your Veterans Service Organization';
    case STATUS_TYPES.decisionInProgress:
      return 'A judge is reviewing your appeal';
    case STATUS_TYPES.bvaDevelopment:
      return 'The judge is seeking more information before making a decision';
    case STATUS_TYPES.stayed:
      return 'The Board is waiting until a higher court makes a decision';
    case STATUS_TYPES.remand:
    case STATUS_TYPES.amaRemand:
    case STATUS_TYPES.bvaDecision:
      return 'The Board made a decision on your appeal';
    case STATUS_TYPES.fieldGrant:
      return `The ${aojDescription} granted your appeal`;
    case STATUS_TYPES.withdrawn:
      return 'You withdrew your appeal';
    case STATUS_TYPES.ftr:
      return 'Your appeal was closed';
    case STATUS_TYPES.ramp:
      return 'You opted in to the Rapid Appeals Modernization Program (RAMP)';
    case STATUS_TYPES.reconsideration:
      return 'Your Motion for Reconsideration was denied';
    case STATUS_TYPES.death:
      return 'The appeal was closed';
    case STATUS_TYPES.otherClose:
      return 'Your appeal was closed';
    case STATUS_TYPES.merged:
      return 'Your appeal was merged';
    case STATUS_TYPES.statutoryOptIn:
      return 'You requested a decision review under the Appeals Modernization Act';
    case STATUS_TYPES.evidentiaryPeriod:
      return 'Your appeals file is open for new evidence';
    case STATUS_TYPES.postBvaDtaDecision:
    case STATUS_TYPES.bvaDecisionEffectuation:
      return `The ${aojDescription} corrected an error`;
    // 'sc_recieved' is a misspelled status value still emitted by the API
    case 'sc_recieved':
    case STATUS_TYPES.scReceived:
      return 'We received your supplemental claim';
    case STATUS_TYPES.hlrReceived:
      return 'We received your higher level review';
    case STATUS_TYPES.scDecision:
    case STATUS_TYPES.hlrDecision:
      return `The ${aojDescription} made a decision`;
    case STATUS_TYPES.hlrDtaError:
      return `The ${aojDescription} is correcting an error`;
    case STATUS_TYPES.scClosed:
      return 'Your Supplemental Claim was closed';
    case STATUS_TYPES.hlrClosed:
      return 'Your Higher-Level Review was closed';
    case STATUS_TYPES.remandReturn:
      return "Your appeal was returned to the Board of Veterans' Appeals";
    default:
      return "We don't know your status";
  }
}

/**
 * Returns a string with the formatted name of the AMA docket.
 * @param {string} docket
 * @returns {string}
 */
export function getDocketName(docket) {
  switch (docket) {
    case DOCKET_TYPES.directReview:
      return 'Direct Review';
    case DOCKET_TYPES.evidenceSubmission:
      return 'Evidence Submission';
    case DOCKET_TYPES.hearingRequest:
      return 'Hearing Request';
    default:
      return docket;
  }
}

export function getHearingType(type) {
  const typeMaps = {
    video: 'videoconference',
    travel_board: 'travel board', // eslint-disable-line camelcase
    central_office: 'Washington, DC central office', // eslint-disable-line camelcase
  };

  return typeMaps[type] || type;
}

/**
 * Returns a string with the formatted name of the type of appeal.
 * @param {Object} appeal
 * @returns {string}
 */
export function getTypeName(appeal) {
  switch (appeal.type) {
    case APPEAL_TYPES.supplementalClaim:
      return 'supplemental claim';
    case APPEAL_TYPES.higherLevelReview:
      return 'higher-level review';
    case APPEAL_TYPES.legacy:
    case APPEAL_TYPES.appeal:
      return 'appeal';
    default:
      return null;
  }
}

function isInEvidenceGathering(claim) {
  const allowedClaimTypes = ['evss_claims', 'claim'];
  const isEvssClaim = claim.type === 'evss_claims';
  const isLighthouseClaim = claim.type === 'claim';

  if (!allowedClaimTypes.includes(claim.type)) {
    return false;
  }

  if (isEvssClaim) return claim.attributes.phase === 3;
  if (isLighthouseClaim) {
    return claim.attributes.status === 'EVIDENCE_GATHERING_REVIEW_DECISION';
  }

  return false;
}

export function groupClaimsByDocsNeeded(list) {
  const groupingPredicate = c => {
    return c.attributes.documentsNeeded && isInEvidenceGathering(c);
  };

  const claimsWithOpenRequests = list.filter(groupingPredicate);

  const claimsWithoutOpenRequests = list.filter(
    claim => !groupingPredicate(claim),
  );

  return claimsWithOpenRequests.concat(claimsWithoutOpenRequests);
}

/**
 * Determines if an item (appeal or claim) is closed
 * @param {Object} item - Appeal or claim object from different endpoints
 * @returns {boolean} - true if closed, false if open
 *
 * Data sources:
 * 1. Appeals API (/appeals): Uses attributes.active boolean
 * 2. Benefits Claims API (/benefits_claims): Uses attributes.status string
 * 3. CHAMPVA claims (ivc_champva provider, via /benefits_claims): PEGA
 *    statuses are normalized by the backend; 'vbms' indicates the
 *    application has been decided
 */
export function isClosed(item) {
  if (!item || !item.attributes) {
    return false;
  }

  if (appealTypes.includes(item.type)) {
    return item.attributes.active === false;
  }

  return (
    item.attributes.status?.toLowerCase() === 'complete' ||
    item.attributes.status === 'vbms'
  );
}

/**
 * Returns the list card label text for a claim/appeal item.
 * @param {Object} item - Claim or appeal item
 * @returns {'Closed'|'In Progress'}
 */
export function getListItemLabel(item) {
  return isClosed(item) ? 'Closed' : 'In Progress';
}

export function isDisabilityCompensationClaim(claimTypeCode) {
  return disabilityCompensationClaimTypeCodes.includes(claimTypeCode);
}

export function isEightStepPensionClaim(claimTypeCode) {
  return eightStepPensionClaimTypeCodes.includes(claimTypeCode);
}

// When feature flag cstClaimPhases is enabled and claim type code is for a
// disability compensation claim or an 8-step pension claim, we show 8 phases
// instead of 5 with updated descriptions, link text, and statuses.
export function getShowEightPhases(claimTypeCode, cstClaimPhasesEnabled) {
  return (
    cstClaimPhasesEnabled &&
    (isDisabilityCompensationClaim(claimTypeCode) ||
      isEightStepPensionClaim(claimTypeCode))
  );
}

// Builds the link to a claim's detail page. useVaTabs is the
// cstClaimDetailsVaTabs feature flag.
export function claimDetailHref(
  claimId,
  { provider = null, useVaTabs = false } = {},
) {
  const base = `/track-claims/your-claims/${claimId}`;

  if (!useVaTabs) {
    return provider ? `${base}/status?type=${provider}` : `${base}/status`;
  }

  const search = new URLSearchParams(provider ? { type: provider } : {});
  search.set('tab', 'status');
  return `${base}?${search}`;
}

// Returns true if the claim has loaded attributes (Adding !! to coerce to boolean).
export const claimAvailable = claim =>
  !!(claim && claim.attributes && Object.keys(claim.attributes).length !== 0);

// Capitalizes the first character of a string.
export const sentenceCase = str => {
  return typeof str === 'string' && str.length > 0
    ? str[0].toUpperCase().concat(str.substring(1))
    : '';
};

export function getClaimType(claim) {
  if (claim?.attributes?.claimType) {
    const { claimType } = claim.attributes;
    return claimType === 'Death'
      ? 'expenses related to death or burial'
      : claimType;
  }

  return 'Disability Compensation';
}

// Handles server-generated claim titles (cst_use_claim_title_generator_web feature flag).
// When backend flag is ON, both displayTitle and claimTypeBase will be present.
//   - List/detail: uses displayTitle directly
//   - Breadcrumb/document: composes using claimTypeBase with tab name and date
export function generateServerClaimTitle(claim, placement, tab) {
  const { displayTitle, claimTypeBase, claimDate } = claim.attributes;

  // For list/detail views: use displayTitle directly (already formatted by backend)
  if (!placement || placement === 'detail') {
    return displayTitle;
  }

  // For breadcrumb/document: compose using claimTypeBase
  const tabPrefix = `${tab} ${tab === 'Files' ? 'for' : 'of'}`;

  if (placement === 'breadcrumb') {
    return `${tabPrefix} your ${claimTypeBase}`;
  }

  if (placement === 'document') {
    const formattedDate = buildDateFormatter()(claimDate);
    return titleCase(`${tabPrefix} ${formattedDate} ${claimTypeBase}`);
  }

  // Fallback to displayTitle for any unexpected placement
  return displayTitle;
}

export const generateAppealTitle = (appeal, tab) => {
  const { attributes } = appeal;

  const types = {
    appeal: 'Appeal',
    higherLevelReview: 'Higher-Level Review',
    legacyAppeal: 'Appeal',
    supplementalClaim: 'Supplemental Claim',
  };

  const tabPrefix = tab ? `${sentenceCase(tab)} for` : 'Information for';
  const date = attributes?.updated
    ? buildDateFormatter()(attributes?.updated)
    : '';

  const appealType = appeal?.type ? types[appeal?.type] : '';

  if (!appealType) {
    return `${tabPrefix} your claim`;
  }

  return `${tabPrefix} ${date} ${appealType}`.trim();
};

// Returns a title for a claim for the specified placement:
//   'detail' for the heading on the single page view
//   'breadcrumb' for the breadcrumbs on the single page view
//   'document' for the browser tab title on the single page view
//   the default return is for the list view (card heading)
export const generateClaimTitle = (claim, placement, tab) => {
  if (claim?.attributes?.appealIds) {
    return generateAppealTitle(claim, tab);
  }

  // Check if server provides title fields (feature flag ON)
  if (claim?.attributes?.displayTitle && claim?.attributes?.claimTypeBase) {
    return generateServerClaimTitle(claim, placement, tab);
  }

  // Legacy client-side title generation (feature flag OFF)
  // This will default to 'disability compensation'
  const claimType = getClaimType(claim).toLowerCase();
  const isRequestToAddOrRemoveDependent =
    addOrRemoveDependentClaimTypeCodes.includes(
      claim?.attributes?.claimTypeCode,
    );

  // Determine which word should follow the tab name.
  // "Files for...", "Status of...", "Details of...", "Overview of..."
  const tabPrefix = `${tab} ${tab === 'Files' ? 'for' : 'of'}`;

  // Use the following to (somewhat) cut down on repetition in the switch below.
  const addOrRemoveDependentClaimTitle = 'request to add or remove a dependent';
  const baseClaimTitle = isRequestToAddOrRemoveDependent
    ? addOrRemoveDependentClaimTitle
    : `${claimType} claim`;

  const renderTitle = () => {
    if (isRequestToAddOrRemoveDependent) {
      return sentenceCase(addOrRemoveDependentClaimTitle);
    }

    if (pensionClaimTypeCodes.includes(claim?.attributes?.claimTypeCode)) {
      const { claimTypeCode } = claim.attributes;
      if (survivorsPensionClaimTypeCodes.includes(claimTypeCode)) {
        return 'Claim for Survivors Pension';
      }

      if (DICClaimTypeCodes.includes(claimTypeCode)) {
        return 'Claim for Dependency and Indemnity Compensation';
      }

      if (veteransPensionClaimTypeCodes.includes(claimTypeCode)) {
        return 'Claim for Veterans Pension';
      }

      return 'Claim for pension';
    }

    return `Claim for ${claimType}`;
  };

  switch (placement) {
    case 'detail':
      return renderTitle();
    case 'breadcrumb':
      if (claimAvailable(claim)) {
        return `${tabPrefix} your ${baseClaimTitle}`;
      }
      return `${tabPrefix} your claim`;
    case 'document':
      if (claimAvailable(claim)) {
        const formattedDate = buildDateFormatter()(claim.attributes.claimDate);
        return titleCase(`${tabPrefix} ${formattedDate} ${baseClaimTitle}`);
      }
      return `${tabPrefix} Your Claim`;
    default:
      return renderTitle();
  }
};

// Returns true if the claim's `provider` attribute matches a known CHAMPVA
// provider identifier.
export const isChampvaProviderClaim = claim =>
  CHAMPVA_PROVIDERS.has(claim?.attributes?.provider);

// CHAMPVA-aware claim status meta handling. When the cstChampvaCustomContent
// feature flag is enabled, CHAMPVA claims keep their claimStatusMeta
// overrides. When disabled, the overrides are stripped so CHAMPVA claims
// render with the default (non-CHAMPVA) content.
export const shouldUseClaimStatusMeta = (
  claim,
  cstChampvaCustomContentEnabled,
) => cstChampvaCustomContentEnabled || !isChampvaProviderClaim(claim);

export const withClaimStatusMetaIfEnabled = (
  claim,
  cstChampvaCustomContentEnabled,
) => {
  if (!claim?.attributes) return claim;
  if (shouldUseClaimStatusMeta(claim, cstChampvaCustomContentEnabled)) {
    return claim;
  }

  const attributesWithoutMeta = { ...claim.attributes };
  delete attributesWithoutMeta.claimStatusMeta;
  return {
    ...claim,
    attributes: attributesWithoutMeta,
  };
};

const getAppealDate = appeal => {
  const { events } = appeal.attributes;
  return events && events.length ? events[events.length - 1].date : '0';
};

const getClaimDate = claim => {
  const { claimPhaseDates, phaseChangeDate } = claim.attributes;
  return phaseChangeDate || claimPhaseDates?.phaseChangeDate || '0';
};

const getDate = item => {
  if (!item.attributes) {
    return '0';
  }

  return appealTypes.includes(item.type)
    ? getAppealDate(item)
    : getClaimDate(item);
};

/**
 * @param {Object} item1
 * @param {Object} item2
 * @returns {-1|1|0}
 */
export function sortByLastUpdated(item1, item2) {
  const date1 = new Date(getDate(item1)).getTime();
  const date2 = new Date(getDate(item2)).getTime();

  if (date1 > date2) {
    return -1;
  }
  if (date1 < date2) {
    return 1;
  }
  return 0;
}
